import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatSnackBar } from '@angular/material';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';

/**
 * Dialog para transferir una alarma a otra area. Cascada de 4 niveles:
 *   1. Area           (area_agente filtrada por idarea de la cita o del usuario)
 *   2. Tipologia      (hijas nivel 1 de los escenarios asignados al area seleccionada)
 *   3. Subtipologia   (hijas nivel 2)
 *   4. Razon          (hijas nivel 3, opcional)
 *
 * data:
 *   {
 *     id_cita: number,
 *     area_id?: number     // opcional, si no viene se resuelve via id_cita
 *   }
 *
 * Retorna al confirmar:
 *   {
 *     resp: true,
 *     idareaagente_destino: number,
 *     idtipologia_agente: number,
 *     idsubtipologia_agente: number,
 *     idrazon_agente: number | null
 *   }
 */
@Component({
  selector: 'app-alarma-transferir-dialog',
  templateUrl: './alarma-transferir-dialog.component.html',
  styleUrls: ['./alarma-transferir-dialog.component.scss']
})
export class AlarmaTransferirDialogComponent implements OnInit {

  form: FormGroup;
  cargando = false;
  mensaje = '¿A qué área desea transferir la alarma?';

  // Nivel 1: area_agente disponibles (segun idarea de la cita/usuario)
  areas: any[] = [];

  // Catalogo plano de tipologias del area seleccionada
  todas: any[] = [];
  tipologias: any[] = [];
  subtipologias: any[] = [];
  razones: any[] = [];

  constructor(
    private fb: FormBuilder,
    private gateway: GatewayService,
    private snackbar: MatSnackBar,
    private localSave: LocalSaveService,
    public dialogRef: MatDialogRef<AlarmaTransferirDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.form = this.fb.group({
      idareaagente:   [null, Validators.required],
      idtipologia:    [null, Validators.required],
      idsubtipologia: [null, Validators.required],
      idrazon:        [null]
    });
  }

  ngOnInit() {
    this.cargarAreas();
  }

  /**
   * Carga las area_agente filtradas por idarea. Prioridad:
   *   1. data.area_id (area de la cita)
   *   2. IDAREA del usuario logueado
   */
  cargarAreas() {
    this.cargando = true;
    const user = this.localSave.get('usuarioPGV') || {};
    const idarea = this.data.area_id || user.IDAREA || null;
    // Se excluye el area_agente del usuario actual para que no se pueda
    // "transferir" a si mismo.
    const excluir = user.idareaagente || null;
    this.gateway.get('getAreasTransferibles', {
      idarea: idarea,
      id_cita: this.data.id_cita,
      excluir_idareaagente: excluir,
      tipo_escenario: this.data.tipo_escenario || null
    }).then((res: any) => {
      this.areas = Array.isArray(res.rows) ? res.rows : [];
      this.cargando = false;
    }).catch(() => {
      this.areas = [];
      this.cargando = false;
    });
  }

  /**
   * Al elegir un area, cargar las tipologias de los escenarios que esa area
   * tiene asignados en escenario_area.
   */
  onAreaChange(idareaagente: any) {
    this.todas = [];
    this.tipologias = [];
    this.subtipologias = [];
    this.razones = [];
    this.form.patchValue({
      idtipologia: null,
      idsubtipologia: null,
      idrazon: null
    });
    if (!idareaagente) { return; }
    this.cargando = true;
    this.gateway.get('getTipologiasPorAreaAgente', {
      idareaagente: idareaagente,
      tipo_escenario: this.data.tipo_escenario || null,
      // idarea de la cita (14 Inst / 15 Rep) para filtrar el arbol de tipologias
      idarea: this.data.area_id || null
    }).then((res: any) => {
      this.todas = Array.isArray(res.rows) ? res.rows : [];
      this.tipologias = this.todas.filter(t => Number(t.nivel_tipologia) === 1);
      this.cargando = false;
    }).catch(() => {
      this.todas = [];
      this.tipologias = [];
      this.cargando = false;
    });
  }

  onTipologiaChange(idTip: any) {
    this.subtipologias = this.todas.filter(t =>
      Number(t.idtipologiapadre) === Number(idTip) && Number(t.nivel_tipologia) === 2
    );
    this.razones = [];
    this.form.patchValue({ idsubtipologia: null, idrazon: null });
  }

  onSubtipologiaChange(idSub: any) {
    this.razones = this.todas.filter(t =>
      Number(t.idtipologiapadre) === Number(idSub) && Number(t.nivel_tipologia) === 3
    );
    this.form.patchValue({ idrazon: null });
  }

  puedeConfirmar(): boolean {
    const v = this.form.value;
    if (!v.idareaagente || !v.idtipologia || !v.idsubtipologia) { return false; }
    if (this.razones.length > 0 && !v.idrazon) { return false; }
    return true;
  }

  confirmar() {
    if (!this.puedeConfirmar()) {
      this.snackbar.open('Complete todos los campos', 'Cerrar', { duration: 2500 });
      return;
    }
    const v = this.form.value;
    this.dialogRef.close({
      resp: true,
      idareaagente_destino: Number(v.idareaagente) || null,
      idtipologia_agente: Number(v.idtipologia) || null,
      idsubtipologia_agente: Number(v.idsubtipologia) || null,
      idrazon_agente: v.idrazon ? Number(v.idrazon) : null
    });
  }

  cancelar() {
    this.dialogRef.close({ resp: false });
  }
}
